import { throwError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Invoice, ProfileDetail } from '@externalusermodel/Invoice';
import { Station, AccountExecutives, ChaseUIDRequest, ChaseAdvancePaymentUIDRequest, StationList } from '@externalusermodel/AdvancePayment';
import { environment } from '@environment/environment';
import { AdvancePaymentDetails } from '@externalusermodel/AdvancePaymentDetails';
import { TransactionHistory, TransactionHistoryUpdate } from '@externalusermodel/Order';
import { ChaseProfileTransactionData } from '@externalusermodel/ChaseProfileTransactionData';
import { ChaseProfileTransactionDetails } from '@externalusermodel/ChaseProfileTransactionDetails';
import { AgencyDetail } from '@externalusermodel/AgencyDetail';

@Injectable({
  providedIn: 'root',
})
export class ExternalUserService {
  private baseUrl: string = ((environment as any).stage && (environment as any).stage.Invoiceservicebaseurl)
    ? (environment as any).stage.Invoiceservicebaseurl
    : (environment as any).Invoiceservicebaseurl;

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const accessToken = sessionStorage.getItem('token') || sessionStorage.getItem('access_token');
    const userId = sessionStorage.getItem('userid');

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
      headers.token = accessToken;
    }

    if (userId) {
      headers.useridno = userId;
    }

    return new HttpHeaders(headers);
  }

  GetInvoiceGrid(agencyID: string): Observable<any[]> {
    const headers = this.getHeaders();
    return this.http
      .get<any[]>(`${this.baseUrl}/api/Invoice/${agencyID}`, { headers })
      .pipe(catchError(this.handleError));
  }

  GetOrderGrid(agencyID: string): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/api/Order/${agencyID}`)
      .pipe(catchError(this.handleError));
  }

  GetInvoiceDetail(
    totalAmount: number,
    agencyID: string,
    paymentType: string,
    chaseOrderId: string,
    history: any[]
  ): Observable<any[]> {
    const headers = this.getHeaders();
    return this.http
      .post<any[]>(
        `${this.baseUrl}/api/InvoiceCalc/${totalAmount}/${agencyID}/${paymentType}/${chaseOrderId}`,
        history,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  GetOrderDiscountDetail(
    orderTotalAmount: number,
    agencyID: string,
    tax: number,
    agencyComm: number,
    paymentType: string,
    orders: any[]
  ): Observable<any[]> {
    const headers = this.getHeaders();
    return this.http
      .post<any[]>(
        `${this.baseUrl}/api/Order/PayAmountCalc/${orderTotalAmount}/${agencyID}/${tax}/${agencyComm}/${paymentType}`,
        orders,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  GetAdvanceDiscountDetail(advanceTotalAmount: number): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/api/AdvanceCalc/${advanceTotalAmount}`)
      .pipe(catchError(this.handleError));
  }

  GetAdvanceAgencyDetail(
    advanceTotalAmount: number,
    agencyID: string,
    paymentType: string,
    advancePaymentDetailsId: string,
    isTaxChecked: boolean,
    isCommissionChecked: boolean
  ): Observable<any> {
     const headers = this.getHeaders();
    return this.http
      .get<any>(
        `${this.baseUrl}/api/AdvanceCalc/${advanceTotalAmount}/${agencyID}/${paymentType}/${advancePaymentDetailsId}/${isTaxChecked}/${isCommissionChecked}`,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  updateCommsandTaxPercent(
    paymentType: string,
    advancePaymentDetailsId: string,
    isTaxChecked: boolean
  ): Observable<any[]> {
    const headers = this.getHeaders();
    return this.http
      .post<any[]>(
        `${this.baseUrl}/api/updateCommsandTaxPercent/${paymentType}/${advancePaymentDetailsId}/${isTaxChecked}`,
        { advancePaymentDetailsId },
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  updateCommsPercent(
    paymentType: string,
    advancePaymentDetailsId: string
  ): Observable<any[]> {
    const headers = this.getHeaders();
    return this.http
      .post<any[]>(
        `${this.baseUrl}/api/updateCommsPercent/${paymentType}/${advancePaymentDetailsId}`,
        { advancePaymentDetailsId },
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  getStations(): Observable<Station[]> {
    const headers = this.getHeaders();
    return this.http
      .get<Station[]>(`${this.baseUrl}/api/AdvancePayments/Station`, { headers })
      .pipe(catchError(this.handleError));
  }

  getAccountExecutives(): Observable<AccountExecutives[]> {
    const headers = this.getHeaders();
    return this.http
      .get<AccountExecutives[]>(
        `${this.baseUrl}/api/AdvancePayments/AccountExecutives`,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  GetOrderInvoiceGrid(orderInvoiceId: string, agencyID: string): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/api/OrderInvoice/${agencyID}/${orderInvoiceId}`)
      .pipe(catchError(this.handleError));
  }

  getProfileList(userID: string, type: string): Observable<ProfileDetail[]> {
    return this.http
      .get<ProfileDetail[]>(`${this.baseUrl}/api/GetProfile/${userID}/${type}`)
      .pipe(catchError(this.handleError));
  }

  saveAdvancePaymentDetails(
    advancePaymentDetailsId: any
  ): Observable<number> {
    const headers = this.getHeaders();
    return this.http
      .post<number>(
        `${this.baseUrl}/api/advancePayments/saveAdvancePaymentDetails`,
        advancePaymentDetailsId,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  getAdvancePaymentDetails(chaseOrderId: string): Observable<string> {
    return this.http
      .get<string>(`${this.baseUrl}/api/getAdvancePaymentDetails/${chaseOrderId}`)
      .pipe(catchError(this.handleError));
  }

  saveTransactionHistory(
    transaction: TransactionHistory[]
  ): Observable<string> {
    const headers = this.getHeaders();
    return this.http
      .post<string>(
        `${this.baseUrl}/api/Order/saveTransactionHistory`,
        transaction,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  getTransactionHistory(
    chaseOrderId: string
  ): Observable<TransactionHistory[]> {
    return this.http
      .get<TransactionHistory[]>(
        `${this.baseUrl}/api/Order/getTransactionHistory/${chaseOrderId}`
      )
      .pipe(catchError(this.handleError));
  }

  GetOrderUId(uIDRequestData: ChaseUIDRequest): Observable<any> {
    const headers = this.getHeaders();
    return this.http
      .post<any>(
        `${this.baseUrl}/api/order/getUID`,
        uIDRequestData,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  ChaseProfileTransaction(
    transactionData: ChaseProfileTransactionData
  ): Observable<any> {
    const headers = this.getHeaders();
    return this.http
      .post<any>(
        `${this.baseUrl}/api/profile/profileTransaction`,
        transactionData,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  getTransactionDetailsFromChaseOrderId(
    chaseOrderId: string
  ): Observable<ChaseProfileTransactionDetails> {
    return this.http
      .get<ChaseProfileTransactionDetails>(
        `${this.baseUrl}/api/profile/getTransactionDetailsFromChaseOrderId/${chaseOrderId}`
      )
      .pipe(catchError(this.handleError));
  }

  updateTransactionHistory(
    requestData: TransactionHistoryUpdate
  ): Observable<string> {
    const headers = this.getHeaders();
    return this.http
      .post<string>(
        `${this.baseUrl}/api/order/updateTransactionHistory`,
        requestData,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  getTransactionAmountFromChaseOrderId(
    chaseOrderId: string
  ): Observable<string> {
    return this.http
      .get<string>(
        `${this.baseUrl}/api/order/getTransactionAmountFromChaseOrderId/${chaseOrderId}`
      )
      .pipe(catchError(this.handleError));
  }

  getAgencyList(userID: string): Observable<AgencyDetail[]> {
    const headers = this.getHeaders();
    return this.http
      .get<AgencyDetail[]>(`${this.baseUrl}/api/getAgencyList/${userID}`, {
        headers,
      })
      .pipe(catchError(this.handleError));
  }

  getDownloadUrl(id: string): Observable<string> {
    return this.http
      .get<string>(`${this.baseUrl}/api/aws/getDownloadUrl/${id}`)
      .pipe(catchError(this.handleError));
  }

  SaveAdvancePaymentstations(
    advancePaymentstationsDetails: any
  ): Observable<any[]> {
    const headers = this.getHeaders();
    return this.http
      .post<any[]>(
        `${this.baseUrl}/api/SaveAdvancePaymentstations`,
        advancePaymentstationsDetails,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  GetStationGrid(userID: string): Observable<StationList[]> {
    return this.http
      .get<StationList[]>(
        `${this.baseUrl}/api/GetAdvancePaymentStationList/${userID}`
      )
      .pipe(catchError(this.handleError));
  }

  UpdateAdvancePaymentstation(stationDetails: any): Observable<any[]> {
    const headers = this.getHeaders();
    return this.http
      .post<any[]>(
        `${this.baseUrl}/api/UpdateAdvancePaymentstations`,
        stationDetails,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  DeleteAdvancePaymentstation(stationDetails: any): Observable<any[]> {
    const headers = this.getHeaders();
    return this.http
      .post<any[]>(
        `${this.baseUrl}/api/DeleteAdvancePaymentstations`,
        stationDetails,
        { headers }
      )
      .pipe(catchError(this.handleError));
  }

  getPayInformation(userID: string): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/api/GetAdvancePayment/${userID}`)
      .pipe(catchError(this.handleError));
  }

  private handleError(error: any): Observable<never> {
    const errorMsg = error.message || 'An unknown error occurred';
    console.error(errorMsg);
    return throwError(() => new Error(errorMsg));
  }
}